/**
 * O texto do jeito que a busca procura: sem acento e em minúscula.
 *
 * Precisa combinar com a função `sem_acento` do banco, que mantém as colunas
 * `nome_busca`, `titulo_busca` e `conteudo_busca`. Procurar "fabio" acha
 * "Fábio" porque os dois lados passam pela mesma regra; se um lado mudasse, a
 * busca simplesmente pararia de achar, sem erro nenhum na tela.
 *
 * `NFD` separa a letra do acento, e a faixa apagada é a dos acentos. O "ç" vira
 * "c" pelo mesmo caminho, como no banco.
 */
export function semAcento(texto: string): string {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

/**
 * O termo pronto para ir num `LIKE`, com o que é curinga virando texto comum.
 *
 * Sem isto, procurar "50%" traz a base inteira, e "_" casa com qualquer letra.
 * Ninguém digita curinga de propósito numa caixa de busca.
 */
export function comoTermoDeBusca(texto: string): string {
  return semAcento(texto).replace(/[\\%_]/g, (achado) => `\\${achado}`)
}

/** O mesmo termo já cercado de `%`, que é como toda busca daqui procura. */
export function contendoTermo(texto: string): string {
  return `%${comoTermoDeBusca(texto)}%`
}

/**
 * A palavra pronta para o Postgres procurar inteira, como foi escrita.
 *
 * Buscar em mensagens é outra pergunta que buscar contato: ali o nome pode ter
 * sido cadastrado de qualquer jeito, aqui a palavra é o assunto. "gengiva"
 * acha "gengiva" e não "gengival"; acento conta, porque quem procura "prótese"
 * está procurando a palavra, não um contato.
 *
 * `\m` e `\M` são o começo e o fim de palavra do Postgres. O resto do texto
 * digitado tem os símbolos de expressão regular neutralizados: sem isso um
 * ponto acharia qualquer letra e um parêntese solto derrubaria a consulta.
 */
export function comoPalavraInteira(texto: string): string {
  const escapado = texto.trim().replace(/[.*+?^${}()|[\]\\]/g, (achado) => `\\${achado}`)
  return `\\m${escapado}\\M`
}

/**
 * A mesma palavra inteira, agora como expressão regular do JavaScript.
 *
 * Serve para destacar a palavra dentro da mensagem na tela, com a mesma regra
 * que o banco usou para achar a conversa: o que está destacado é o que foi
 * procurado, nem mais nem menos.
 *
 * As bordas olham por letra ou número de qualquer idioma, e não pelo `\b` do
 * JavaScript, que considera "é" fim de palavra e partiria "prótese" ao meio.
 */
export function regexDePalavraInteira(palavra: string): RegExp {
  const escapada = palavra.trim().replace(/[.*+?^${}()|[\]\\]/g, (achado) => `\\${achado}`)
  return new RegExp(`(?<![\\p{L}\\p{N}])${escapada}(?![\\p{L}\\p{N}])`, 'giu')
}
